import { clsx } from "clsx";
import {
	CalendarPlus,
	Check,
	ChevronDown,
	ChevronRight,
	ClipboardPen,
	TriangleAlert,
} from "lucide-react";
import {
	type MouseEvent as ReactMouseEvent,
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useEffect,
	useRef,
} from "react";

import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { MeasureButton } from "@/features/measurements/components/measure-button";
import { NoteRowIcon } from "@/features/schedule-notes/components/note-row-icon";
import type { NoteSeverity } from "@/features/schedule-notes/types.ts";
import type { ScheduleNode } from "../../types.ts";
import type { Tone } from "../../utils/colors.ts";
import type { DelayInfo } from "../../utils/delay.ts";
import {
	formatActualRange,
	formatDays,
	formatDurationDelta,
	formatScheduleRange,
	scheduleDays,
} from "../../utils/gantt.ts";
import { dateAtX, PX_PER_DAY, type TimeScale } from "../../utils/scale.ts";
import {
	dayIndexToDate,
	dateToDayIndex,
	type TimelineDomain,
	timelineBar,
} from "../../utils/timeline.ts";
import type { ScheduleDraft } from "../schedule-editor";

export const SCROLL_EDGE = 240;
const AUTO_SCROLL_ZONE = 48;
const AUTO_SCROLL_MAX_STEP = 24;
const LABEL_MIN_PX = 96;

export interface DragState {
	stageId: string;
	anchor: string;
	current: string;
}

export interface RowModel {
	node: ScheduleNode;
	childCount: number;
	isLeaf: boolean;
	collapsed: boolean;
	delay: DelayInfo;
	percent: number;
	tone: Tone;
	severity?: NoteSeverity;
}

function autoScrollStep(depth: number): number {
	const factor = Math.min(depth / AUTO_SCROLL_ZONE, 1);
	return Math.max(4, Math.round(factor * AUTO_SCROLL_MAX_STEP));
}

function mondayIndex(date: Date): number {
	return (date.getDay() + 6) % 7;
}

function isInteractive(target: EventTarget | null): boolean {
	return (
		target instanceof Element &&
		!!target.closest("button, .schedule-gantt__bar, .schedule-gantt__actual")
	);
}

interface TrackProps {
	row: RowModel;
	domain: TimelineDomain;
	scale: TimeScale;
	canManage: boolean;
	canMeasure: boolean;
	todayAt: number | null;
	todayKey: string;
	drag: DragState | null;
	preview: ScheduleDraft | null;
	scrollRef: RefObject<HTMLDivElement | null>;
	onDragChange: (drag: DragState | null) => void;
	onDraft: (draft: ScheduleDraft) => void;
	onExtend: (side: "before" | "after") => void;
	onActual: (node: ScheduleNode) => void;
}

function GanttTrack({
	row,
	domain,
	scale,
	canManage,
	canMeasure,
	todayAt,
	todayKey,
	drag,
	preview,
	scrollRef,
	onDragChange,
	onDraft,
	onExtend,
	onActual,
}: TrackProps) {
	const { node, isLeaf, delay, percent, tone } = row;
	const px = PX_PER_DAY[scale];
	const editable = isLeaf && canManage;
	const dragEnabled = editable && scale === "days";
	const bar = timelineBar(domain, node.start, node.end);
	const duration = scheduleDays(node.start, node.end);
	const actualBar = node.actualStart
		? timelineBar(domain, node.actualStart, node.actualEnd ?? todayKey)
		: null;
	const actualOpen = !!node.actualStart && !node.actualEnd;
	const actualDays = scheduleDays(node.actualStart, node.actualEnd);
	const extensionStart =
		isLeaf && delay.kind === "delayed" && node.end
			? dayIndexToDate(domain, (dateToDayIndex(domain, node.end) ?? 0) + 1)
			: null;
	const extension = extensionStart
		? timelineBar(domain, extensionStart, todayKey)
		: null;
	const ownDraft: ScheduleDraft = {
		stageId: node.id,
		start: node.ownStart,
		end: node.ownEnd,
	};

	const trackRef = useRef<HTMLDivElement>(null);
	const pointerXRef = useRef<number | null>(null);
	const dragRef = useRef(drag);
	dragRef.current = drag;
	const isDragging = drag?.stageId === node.id;

	function dateFromClientX(clientX: number, track: HTMLElement): string {
		const rect = track.getBoundingClientRect();
		return dateAtX(domain, clientX - rect.left, scale);
	}

	function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
		if (!dragEnabled || event.button !== 0 || event.pointerType !== "mouse") {
			return;
		}
		if (isInteractive(event.target)) return;
		const day = dateFromClientX(event.clientX, event.currentTarget);
		pointerXRef.current = event.clientX;
		event.currentTarget.setPointerCapture(event.pointerId);
		onDragChange({ stageId: node.id, anchor: day, current: day });
	}

	function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
		if (!drag || drag.stageId !== node.id) return;
		pointerXRef.current = event.clientX;
		const day = dateFromClientX(event.clientX, event.currentTarget);
		if (day !== drag.current) onDragChange({ ...drag, current: day });
	}

	function handlePointerUp() {
		if (!drag || drag.stageId !== node.id) return;
		const [start, end] = [drag.anchor, drag.current].sort();
		onDragChange(null);
		onDraft({ stageId: node.id, start, end });
	}

	function handleClick(event: ReactMouseEvent<HTMLDivElement>) {
		if (scale !== "weeks" || !editable || bar) return;
		if (isInteractive(event.target)) return;
		const day = dateFromClientX(event.clientX, event.currentTarget);
		onDraft({ stageId: node.id, start: day, end: day });
	}

	useEffect(() => {
		if (!isDragging) return;
		let frame = 0;
		function step() {
			const scroll = scrollRef.current;
			const track = trackRef.current;
			const active = dragRef.current;
			const pointerX = pointerXRef.current;
			if (scroll && track && active && pointerX !== null) {
				const viewport = scroll.getBoundingClientRect();
				const fixed = track.previousElementSibling;
				const stickyWidth =
					fixed instanceof HTMLElement ? fixed.offsetWidth : 0;
				const leftBound = viewport.left + stickyWidth;
				let delta = 0;
				if (pointerX < leftBound + AUTO_SCROLL_ZONE) {
					delta = -autoScrollStep(leftBound + AUTO_SCROLL_ZONE - pointerX);
				} else if (pointerX > viewport.right - AUTO_SCROLL_ZONE) {
					delta = autoScrollStep(
						pointerX - (viewport.right - AUTO_SCROLL_ZONE),
					);
				}
				if (delta !== 0) {
					scroll.scrollLeft += delta;
					if (delta < 0 && scroll.scrollLeft < SCROLL_EDGE) {
						onExtend("before");
					} else if (
						delta > 0 &&
						scroll.scrollLeft + scroll.clientWidth >
							scroll.scrollWidth - SCROLL_EDGE
					) {
						onExtend("after");
					}
					const day = dateFromClientX(pointerX, track);
					if (day !== active.current) {
						onDragChange({ ...active, current: day });
					}
				}
			}
			frame = requestAnimationFrame(step);
		}
		frame = requestAnimationFrame(step);
		return () => cancelAnimationFrame(frame);
	});

	const ghostSource = isDragging
		? drag && [drag.anchor, drag.current]
		: !drag && preview?.stageId === node.id
			? [preview.start, preview.end]
			: null;
	const ghostBounds = ghostSource ? [...ghostSource].sort() : null;
	const ghostBar = ghostBounds
		? timelineBar(domain, ghostBounds[0], ghostBounds[1])
		: null;
	const ghostRange =
		ghostBar && ghostBounds
			? formatScheduleRange(ghostBounds[0], ghostBounds[1])
			: null;

	const showLabel = !!bar && bar.days * px >= LABEL_MIN_PX;
	const label =
		duration !== null ? `${formatDays(duration)} · ${percent}%` : "";
	const plannedStyle = bar
		? {
				left: `${bar.offsetDays * px}px`,
				width: `${bar.days * px}px`,
				"--progress": `${percent}%`,
			}
		: undefined;
	const plannedContent = showLabel ? (
		<span className="schedule-gantt__bar-label">
			{label}
			{percent >= 100 && <Check />}
		</span>
	) : null;

	return (
		<div
			ref={trackRef}
			className={clsx(
				"schedule-gantt__track",
				dragEnabled && "is-editable",
				canManage && !editable && "is-readonly",
			)}
			style={{
				width: `${domain.totalDays * px}px`,
				backgroundPositionX: `${-mondayIndex(domain.start) * px}px, 0`,
			}}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerUp}
			onPointerCancel={() => onDragChange(null)}
			onClick={handleClick}
		>
			{todayAt !== null && (
				<span
					className="schedule-gantt__today-line"
					style={{ left: `${(todayAt + 0.5) * px}px` }}
					aria-hidden
				/>
			)}
			{bar && (
				<Tooltip>
					<TooltipTrigger asChild>
						{editable ? (
							<button
								type="button"
								className="schedule-gantt__bar is-planned"
								data-tone={tone}
								style={plannedStyle}
								onClick={() => onDraft(ownDraft)}
								aria-label={`Editar datas de ${node.name}`}
							>
								{plannedContent}
							</button>
						) : (
							<div
								className="schedule-gantt__bar is-planned"
								data-tone={tone}
								style={plannedStyle}
							>
								{plannedContent}
							</div>
						)}
					</TooltipTrigger>
					<TooltipContent side="top">
						{label}
						{" · "}
						{formatScheduleRange(node.start, node.end)}
						{delay.kind === "delayed" && (
							<>
								<br />
								Atrasada {formatDays(delay.days)}
							</>
						)}
						{delay.kind === "finished_late" && (
							<>
								<br />
								Concluída com {formatDays(delay.days)} de atraso
							</>
						)}
					</TooltipContent>
				</Tooltip>
			)}
			{extension && (
				<>
					<span
						className="schedule-gantt__extension"
						style={{
							left: `${extension.offsetDays * px}px`,
							width: `${extension.days * px}px`,
						}}
						aria-hidden
					/>
					<span
						className="schedule-gantt__extension-label"
						style={{
							left: `${(extension.offsetDays + extension.days) * px}px`,
						}}
					>
						Atrasada {formatDays(delay.days)}
					</span>
				</>
			)}
			{actualBar && (
				<Tooltip>
					<TooltipTrigger asChild>
						{isLeaf && canMeasure ? (
							<button
								type="button"
								className="schedule-gantt__actual"
								data-open={actualOpen ? "true" : undefined}
								style={{
									left: `${actualBar.offsetDays * px}px`,
									width: `${actualBar.days * px}px`,
								}}
								onClick={() => onActual(node)}
								aria-label={`Realizado de ${node.name}`}
							/>
						) : (
							<div
								role="img"
								className="schedule-gantt__actual"
								data-open={actualOpen ? "true" : undefined}
								style={{
									left: `${actualBar.offsetDays * px}px`,
									width: `${actualBar.days * px}px`,
								}}
								aria-label={`Realizado de ${node.name}`}
							/>
						)}
					</TooltipTrigger>
					<TooltipContent side="bottom">
						{formatActualRange(node.actualStart, node.actualEnd)}
						{actualDays !== null && duration !== null && (
							<>
								<br />
								{formatDays(actualDays)} ·{" "}
								{formatDurationDelta(actualDays - duration)}
							</>
						)}
					</TooltipContent>
				</Tooltip>
			)}
			{isLeaf && canMeasure && bar && !node.actualStart && (
				<button
					type="button"
					className="schedule-gantt__actual-entry"
					style={{ left: `${bar.offsetDays * px + 4}px` }}
					onPointerDown={(event) => event.stopPropagation()}
					onClick={() => onActual(node)}
				>
					<ClipboardPen />
					Informar realizado
				</button>
			)}
			{!bar && editable && !ghostBar && (
				<button
					type="button"
					className="schedule-gantt__define"
					style={{ left: `${(todayAt ?? 0) * px + 4}px` }}
					onPointerDown={(event) => event.stopPropagation()}
					onClick={() => {
						const day = dayIndexToDate(domain, todayAt ?? 0);
						onDraft({ stageId: node.id, start: day, end: day });
					}}
				>
					<CalendarPlus />
					Definir datas
				</button>
			)}
			{ghostBar && (
				<span
					className="schedule-gantt__ghost"
					style={{
						left: `${ghostBar.offsetDays * px}px`,
						width: `${ghostBar.days * px}px`,
					}}
					aria-hidden
				>
					{ghostBar.days * px > 150 ? ghostRange : null}
				</span>
			)}
		</div>
	);
}

export function GanttRow({
	row,
	onToggle,
	onMeasure,
	onNotes,
	...track
}: TrackProps & {
	onToggle: (id: string) => void;
	onMeasure: (node: ScheduleNode) => void;
	onNotes: (node: ScheduleNode) => void;
}) {
	const { node, childCount, isLeaf, collapsed, delay, severity } = row;
	const range = formatScheduleRange(node.start, node.end);
	const duration = scheduleDays(node.start, node.end);

	return (
		<div className="schedule-gantt__row" data-stage-id={node.id}>
			<div className="schedule-gantt__fixed">
				<div
					className="schedule-gantt__name"
					style={{ paddingLeft: `${0.5 + node.depth * 0.875}rem` }}
				>
					{severity && (
						<NoteRowIcon
							severity={severity}
							stageName={node.name}
							onClick={() => onNotes(node)}
						/>
					)}
					{childCount > 0 ? (
						<button
							type="button"
							className="schedule-gantt__toggle"
							onClick={() => onToggle(node.id)}
							aria-label={
								collapsed ? `Expandir ${node.name}` : `Recolher ${node.name}`
							}
						>
							{collapsed ? <ChevronRight /> : <ChevronDown />}
						</button>
					) : (
						<span className="schedule-gantt__spacer" aria-hidden />
					)}
					<div className="schedule-gantt__name-text">
						<span className="schedule-gantt__name-label">{node.name}</span>
						<span
							className={clsx(
								"schedule-gantt__name-meta",
								!range && "is-empty",
							)}
						>
							{range ?? "Sem cronograma"}
							{range && !isLeaf ? " · calculado" : ""}
						</span>
						<span className="schedule-gantt__name-meta is-actual">
							{formatActualRange(node.actualStart, node.actualEnd)}
						</span>
					</div>
					{track.canMeasure && isLeaf && (
						<MeasureButton
							className="schedule-gantt__measure"
							stageName={node.name}
							onClick={() => onMeasure(node)}
						/>
					)}
				</div>
				<div className="schedule-gantt__total">
					<span className="schedule-gantt__total-days">
						{duration !== null ? formatDays(duration) : "—"}
					</span>
					{delay.kind === "delayed" && (
						<span className="schedule-gantt__total-delay">
							<TriangleAlert />
							Atrasada {formatDays(delay.days)}
						</span>
					)}
				</div>
			</div>
			<GanttTrack row={row} {...track} />
		</div>
	);
}
